import { CheckCircle, ExternalLink, RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { supabase } from '../../../lib/supabase';
import { useOnboardingStore } from '../../../store/onboarding.store';

interface StripeStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function StripeStep({ onNext, onBack }: StripeStepProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useOnboardingStore();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);

  const checkStripeConnection = useCallback(async (showRefresh = false) => {
    if (!companyId) return;

    if (showRefresh) {
      setIsRefreshing(true);
    }

    try {
      // If returning from Stripe, refresh the account status
      const stripeReturn = searchParams.get('stripe_return');
      if (stripeReturn === 'true' || showRefresh) {
        const { error: refreshError } = await supabase.functions.invoke(
          'refresh-connect-account-status',
          {
            body: { companyId },
          }
        );

        if (refreshError) {
          // Failed to refresh account status - continue with database check
        }

        // Clear the stripe_return param
        if (stripeReturn === 'true') {
          setSearchParams({});
        }
      }

      // Check database for current status
      const { data: companyData, error: fetchError } = await supabase
        .from('companies')
        .select('stripe_connect_account_id, stripe_connect_account_status, stripe_connect_onboarding_complete')
        .eq('id', companyId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (
        companyData?.stripe_connect_account_id &&
        companyData?.stripe_connect_account_status === 'active' &&
        companyData?.stripe_connect_onboarding_complete
      ) {
        setIsStripeConnected(true);
        setConnectStatus('active');
      } else if (companyData?.stripe_connect_account_id) {
        setConnectStatus(companyData.stripe_connect_account_status || 'pending');
      }
    } catch {
      setError('Failed to check Stripe connection status');
    } finally {
      setIsRefreshing(false);
    }
  }, [companyId, searchParams, setSearchParams]);

  useEffect(() => {
    checkStripeConnection(searchParams.get('stripe_return') === 'true');
  }, [checkStripeConnection, searchParams]);

  const handleConnectStripe = async () => {
    if (!companyId) {
      setError('Company ID is required');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Check if Connect account already exists
      const { data: companyData } = await supabase
        .from('companies')
        .select('stripe_connect_account_id')
        .eq('id', companyId)
        .single();

      if (!companyData?.stripe_connect_account_id) {
        // Create Connect account first
        const { data: createResult, error: createError } = await supabase.functions.invoke(
          'create-connect-account',
          {
            body: { companyId },
          }
        );

        if (createError) {
          throw new Error(createError.message || 'Failed to create Stripe Connect account');
        }

        if (!createResult?.success) {
          throw new Error(createResult?.error || 'Failed to create Stripe Connect account');
        }
      }

      // Get onboarding link
      const returnUrl = `${window.location.origin}/onboarding/billing?stripe_return=true`;
      const { data: linkResult, error: linkError } = await supabase.functions.invoke(
        'get-connect-onboarding-link',
        {
          body: {
            companyId,
            refresh: false,
            platform: 'web',
            returnUrl,
          },
        }
      );

      if (linkError) {
        throw new Error(linkError.message || 'Failed to get onboarding link');
      }

      if (!linkResult?.success) {
        throw new Error(linkResult?.error || 'Failed to get onboarding link');
      }

      if (linkResult.url) {
        // Open Stripe onboarding in a new tab or redirect
        window.location.href = linkResult.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect Stripe account';
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        {/* Stripe Logo */}
        <div className="flex items-center justify-center mx-auto mb-4">
          <div className="w-20 h-20 rounded-2xl bg-[#635BFF]/20 flex items-center justify-center border border-[#635BFF]/30">
            <svg viewBox="0 0 468 222.5" className="w-16 h-8" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M414 113.4c0-25.6-12.4-45.8-36.1-45.8-23.8 0-38.2 20.2-38.2 45.6 0 30.1 17 45.3 41.4 45.3 11.9 0 20.9-2.7 27.7-6.5V136c-6.8 3.4-14.6 5.5-24.5 5.5-9.7 0-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-13.9zm-49.4-9.5c0-11.3 6.9-16 13.2-16 6.1 0 12.6 4.7 12.6 16h-25.8zm-42.1 4.5c-6.1 0-9.4-4.2-9.4-9.7 0-5.5 3.3-9.7 9.4-9.7 6.1 0 9.4 4.2 9.4 9.7 0 5.5-3.3 9.7-9.4 9.7zm23.3 1.1h-19.1v71.1h19.1v-71.1zm-70.4-71.1h-18.6l-56 71.1h20.8l56-71.1zm-26.2 0h-19.1v71.1h19.1v-71.1zm-53.4 0h-19.1v71.1h19.1v-71.1zm-26.3 0H93.2l-28.4 71.1h20.1l5.2-13.2h30.8l5.2 13.2h20.1L135.3 38.4zm-19.6 43.6l11.9-30.1 11.9 30.1h-23.8z"
                fill="#635BFF"
              />
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Connect Your Stripe Account</h2>
        <p className="text-[#D1D5DB] mt-2">
          Securely connect your Stripe account to process payments
        </p>
      </div>

      {isRefreshing ? (
        <div className="flex flex-col items-center py-8">
          <Loader2 size={48} className="text-rollops-accent animate-spin mb-4" />
          <p className="text-[#D1D5DB]">Checking connection status...</p>
        </div>
      ) : isStripeConnected ? (
        <div className="space-y-4">
          <div className="bg-green-600/10 border border-green-600/30 rounded-xl p-6 text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-500 mb-2">Stripe Connected!</h3>
            <p className="text-[#D1D5DB]">
              Your Stripe account is successfully connected. You can now accept payments from your members.
            </p>
          </div>

          <button
            onClick={handleConnectStripe}
            className="w-full py-3 px-4 border border-[#504A4A] text-[#D1D5DB] hover:text-[#EBFFEE] hover:border-[#9B1421] font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} />
            Use Different Stripe Account
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-[#363232] rounded-xl p-6">
            <p className="text-[#EBFFEE] mb-4">
              Click the button below to securely connect your Stripe account. You&apos;ll be redirected to Stripe 
              to complete the setup process.
            </p>
            
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm text-[#D1D5DB]">
                <CheckCircle size={14} className="text-green-500" />
                Bank account verification
              </li>
              <li className="flex items-center gap-2 text-sm text-[#D1D5DB]">
                <CheckCircle size={14} className="text-green-500" />
                Identity verification
              </li>
              <li className="flex items-center gap-2 text-sm text-[#D1D5DB]">
                <CheckCircle size={14} className="text-green-500" />
                Tax information
              </li>
            </ul>

            <button
              onClick={handleConnectStripe}
              disabled={isConnecting}
              className="w-full py-4 px-4 bg-[#635BFF] hover:bg-[#5146E5] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isConnecting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink size={18} />
                  Connect Stripe Account
                </>
              )}
            </button>
          </div>

          {connectStatus && connectStatus !== 'active' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-500 font-medium">Setup In Progress</p>
                <p className="text-xs text-[#D1D5DB] mt-1">
                  Your Stripe account is being set up. Please complete any remaining requirements.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={() => checkStripeConnection(true)}
            disabled={isRefreshing}
            className="w-full py-2 text-[#D1D5DB] hover:text-[#EBFFEE] text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={14} />
            Refresh Connection Status
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4 pt-4">
        <button
          onClick={onBack}
          disabled={isConnecting}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={isConnecting || isRefreshing}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
        >
          {isStripeConnected ? 'Continue' : 'Skip for Now'}
        </button>
      </div>
    </div>
  );
}

